// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {withRouter} from 'react-router-dom';
import {connect} from 'react-redux';
import {EllipsisOverflow} from '@deere/isg.component-library';
import {UNIT_OF_MEASURE} from 'Common/constants/preference-constants';
import Panel from 'Ui/components/panel/panel';
import BasicTable from 'Ui/components/common/basic-table/basic-table';
import NoDataMessage from 'Ui/components/panel/no-data-message';
import {useDeepMemo} from 'Utils/react-utils';

const DAYS_PER_YEAR = 365;
const MAX_USAGE_OFFSET = 240;
const MAX_CALENDAR_OFFSET = 30;
const PLAN_TYPE = {
    CUSTOM: 'CUSTOM',
    FACTORY: 'FACTORY'
};
const INTERVAL_TYPE = {
    CALENDAR: 'CALENDAR',
    ENGINE_HOURS: 'ENGINE_HOURS',
    DISTANCE: 'DISTANCE'
};

function getIntervalTypeLabels(translations, unitOfMeasure) {
    let distanceLabel = '';

    if (unitOfMeasure === UNIT_OF_MEASURE.ENGLISH) {
        distanceLabel = translations.mi;
    } else if (unitOfMeasure === UNIT_OF_MEASURE.METRIC) {
        distanceLabel = translations.km;
    }

    return {
        [INTERVAL_TYPE.CALENDAR]: translations.DAY_OR_DAYS.toLowerCase(),
        [INTERVAL_TYPE.ENGINE_HOURS]: translations.ENGINE_HOURS,
        [INTERVAL_TYPE.DISTANCE]: distanceLabel
    };
}

function getEventDaysUntilDue(event, annualUsage) {
    const value = event?.offset?.valueAsDouble;

    if (!Number.isFinite(value)) {
        return Number.POSITIVE_INFINITY;
    }

    if (event.intervalType === INTERVAL_TYPE.CALENDAR) {
        if (value > MAX_CALENDAR_OFFSET) {
            return Number.POSITIVE_INFINITY;
        }

        return value >= 0 ? Math.ceil(value) : Math.floor(value);
    }

    if (event.intervalType === INTERVAL_TYPE.ENGINE_HOURS || event.intervalType === INTERVAL_TYPE.DISTANCE) {
        if (value > MAX_USAGE_OFFSET || !Number.isFinite(annualUsage)) {
            return Number.POSITIVE_INFINITY;
        }

        const dailyUsage = annualUsage / DAYS_PER_YEAR;

        return dailyUsage === 0 ? Number.POSITIVE_INFINITY : value / dailyUsage;
    }

    return Number.POSITIVE_INFINITY;
}

function getDaysUntilDue(plan) {
    const nextDays = getEventDaysUntilDue(plan.mostDueNextMaintenanceEvent, plan.annualUsage);
    const previousDays = getEventDaysUntilDue(plan.mostDuePreviousMaintenanceEvent, plan.annualUsage);
    const useNextEvent = nextDays <= previousDays;

    return {
        title: plan.title,
        type: plan.type,
        event: useNextEvent ?
            plan.mostDueNextMaintenanceEvent :
            plan.mostDuePreviousMaintenanceEvent,
        daysUntilDue: useNextEvent ? nextDays : previousDays
    };
}

function getLowestDuePlan(summaryPlans) {
    return (summaryPlans || [])
        .map(getDaysUntilDue)
        .filter((plan) => Number.isFinite(plan.daysUntilDue))
        .sort((first, second) => {
            const daysDifference = first.daysUntilDue - second.daysUntilDue;

            if (daysDifference !== 0) {
                return daysDifference;
            }

            if (first.type === PLAN_TYPE.CUSTOM && second.type === PLAN_TYPE.FACTORY) {
                return -1;
            }

            if (first.type === PLAN_TYPE.FACTORY && second.type === PLAN_TYPE.CUSTOM) {
                return 1;
            }

            return 0;
        })[0];
}

function createTableCell(value) {
    return (
        <EllipsisOverflow
            slotProps={{
                typography: {
                    component: 'p'
                }
            }}
            style={{
                width: '100%'
            }}
            tooltipPlacement='bottom'
        >
            {value}
        </EllipsisOverflow>
    );
}

function createRows(maintenanceDue, translations, unitOfMeasure) {
    const intervalTypeLabels = getIntervalTypeLabels(translations, unitOfMeasure);

    return (maintenanceDue?.equipment || []).flatMap((equipment) => {
        const plan = getLowestDuePlan(equipment.summaryPlans);

        if (!plan) {
            return [];
        }

        return [{
            row: [
                createTableCell(equipment.equipmentName),
                createTableCell(plan.title),
                Math.trunc(plan.event.offset.valueAsDouble),
                createTableCell(intervalTypeLabels[plan.event.intervalType] ?? '')
            ],
            daysUntilDue: plan.daysUntilDue
        }];
    })
        .sort((first, second) => first.daysUntilDue - second.daysUntilDue)
        .map(({row}) => row);
}

function EquipmentMaintenanceDue(props) {
    const {
        tile: maintenanceDue,
        translations,
        unitOfMeasure
    } = props;

    const headers = [
        translations.NAME,
        translations.PLAN_NAME,
        translations.DUE,
        translations.ONLINK_SERVICE
    ];

    const rows = useDeepMemo(() => createRows(maintenanceDue, translations, unitOfMeasure), [maintenanceDue, translations, unitOfMeasure]);

    return (
        <Panel
            title={translations.ONLINK_MAINTENANCE_DUE}
            translations={translations}
        >
            {
                rows.length > 0 ? (
                    <BasicTable
                        className='maintenance-due-values'
                        headers={headers}
                        rows={rows}
                        vertical={true}
                    />
                ) : <NoDataMessage translations={translations}/>
            }
        </Panel>
    );
}

EquipmentMaintenanceDue.propTypes = {
    tile: PropTypes.shape({
        equipment: PropTypes.arrayOf(PropTypes.object)
    }),
    translations: PropTypes.translations,
    unitOfMeasure: PropTypes.string
};

export function mapStateToProps(state) {
    return {
        unitOfMeasure: state.membership.unitOfMeasure
    };
}

export default connect(mapStateToProps)(withRouter(EquipmentMaintenanceDue));
